/**
 * PRO-5 — Incoming order slices (list).
 *
 * A "slice" is this producer's portion of an order. One buyer order containing goods from three
 * factories fans out into three DISJOINT slices; this producer sees only their own, and has no
 * way to learn that the other two exist.
 *
 * The columns are exactly what `ProducerOrderListItem` carries and nothing more:
 *   order_number · created_at · status · buyer_country · slice_total_usd (at OWN base prices)
 *
 * `buyer_country` is the ONLY buyer attribute a producer ever sees.
 */
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ClipboardList } from 'lucide-react';
import { Button, Card, EmptyState, ErrorState, TableSkeleton } from '@/components/ui';
import { OrderStatusBadge } from '@/components/StatusBadge';
import { qk } from '@/shared/queryClient';
import * as api from '@/shared/api';
import { formatDate, formatUsd } from '@/shared/money';

const PER_PAGE = 25;

export function OrdersScreen() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1');

  const query = useQuery({
    queryKey: qk.orders({ page, per_page: PER_PAGE }),
    queryFn: () => api.listOrders({ page, per_page: PER_PAGE }),
  });

  const total = query.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t('orders.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('orders.subtitle')}</p>
      </div>

      {query.isLoading ? (
        <TableSkeleton rows={5} cols={5} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (query.data?.items.length ?? 0) === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={t('orders.emptyTitle')}
          body={t('orders.emptyBody')}
        />
      ) : (
        <>
          <Card className="overflow-x-auto">
            <table className="w-full min-w-[44rem] text-sm">
              <caption className="sr-only">{t('orders.title')}</caption>
              <thead>
                <tr className="border-b border-border bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('orders.number')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('orders.date')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('orders.status')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('orders.buyerCountry')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-right font-medium">
                    {t('orders.sliceTotal')}
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-hairline">
                {query.data!.items.map((o) => (
                  <tr key={o.order_number} className="hover:bg-muted/60">
                    <td className="px-4 py-3">
                      <Link
                        to={`/orders/${encodeURIComponent(o.order_number)}`}
                        className="font-medium tabular-nums text-foreground hover:text-link hover:underline"
                      >
                        {o.order_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">
                      {formatDate(o.created_at, i18n.language)}
                    </td>
                    <td className="px-4 py-3">
                      <OrderStatusBadge status={o.status} />
                    </td>
                    {/* The only buyer attribute on this screen. */}
                    <td className="px-4 py-3 text-foreground/80">{o.buyer_country}</td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums text-foreground">
                      {formatUsd(o.slice_total_usd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {pages > 1 ? (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t('common.page')} {page} {t('common.of')} {pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setParams({ page: String(page - 1) })}
                >
                  {t('common.back')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pages}
                  onClick={() => setParams({ page: String(page + 1) })}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
